// src/components/SystemRecommendation/components/SystemDiagram.tsx

import React, { useState } from 'react';
import { SystemType, ViewMode } from '../types';

interface SystemDiagramProps {
  systemType: SystemType;
  viewMode: ViewMode;
  basePath?: string; // Optional base path for assets
}

const SystemDiagram: React.FC<SystemDiagramProps> = ({ systemType, viewMode, basePath = '' }) => {
  const [imgOk, setImgOk] = useState(true);
  
  const getDiagramPath = () => {
    // Normalize base path - ensure it doesn't end with slash if non-empty
    const normalizedBase = basePath ? basePath.replace(/\/$/, '') : '';
    const diagrams: Record<SystemType, string> = {
      'combi': `${normalizedBase}/assets/diagrams/combi-boiler.png`,
      'system-boiler': `${normalizedBase}/assets/diagrams/system-boiler-cylinder.png`,
      'heat-pump-air': `${normalizedBase}/assets/diagrams/ashp-diagram.png`,
      'heat-pump-ground': `${normalizedBase}/assets/diagrams/gshp-diagram.png`,
      'regular-boiler': `${normalizedBase}/assets/diagrams/regular-boiler.jpg`
    };

    return diagrams[systemType] || diagrams['system-boiler'];
  };

  const getAnnotations = () => {
    if (viewMode === 'customer') {
      // Simplified annotations for customer view
      const customerAnnotations: Record<SystemType, Array<{x: number, y: number, text: string, type: 'stays' | 'changes' | 'new'}>> = {
        'system-boiler': [
          { x: 50, y: 30, text: 'New boiler', type: 'new' },
          { x: 70, y: 50, text: 'New cylinder', type: 'new' },
          { x: 30, y: 70, text: 'Your radiators stay', type: 'stays' }
        ],
        'heat-pump-air': [
          { x: 40, y: 20, text: 'Heat pump outside', type: 'new' },
          { x: 70, y: 50, text: 'New cylinder', type: 'new' },
          { x: 30, y: 80, text: 'Radiators upgraded', type: 'changes' }
        ],
        'combi': [
          { x: 50, y: 40, text: 'New combi boiler', type: 'new' },
          { x: 30, y: 70, text: 'Your radiators stay', type: 'stays' }
        ],
        'heat-pump-ground': [
          { x: 40, y: 20, text: 'Ground loop', type: 'new' },
          { x: 70, y: 50, text: 'Heat pump + cylinder', type: 'new' }
        ],
        'regular-boiler': [
          { x: 50, y: 30, text: 'New boiler', type: 'new' },
          { x: 70, y: 60, text: 'Tanks in loft', type: 'stays' }
        ]
      };

      return customerAnnotations[systemType] || [];
    }

    // Technical annotations
    const technicalAnnotations: Record<SystemType, Array<{x: number, y: number, text: string, type: 'stays' | 'changes' | 'new'}>> = {
      'system-boiler': [
        { x: 50, y: 30, text: '18kW System Boiler', type: 'new' },
        { x: 70, y: 50, text: '180L Unvented Cylinder', type: 'new' },
        { x: 30, y: 70, text: 'Existing radiators (adequate)', type: 'stays' },
        { x: 50, y: 85, text: '22mm backbone preserved', type: 'stays' }
      ],
      'heat-pump-air': [
        { x: 40, y: 20, text: '12kW ASHP (outdoor)', type: 'new' },
        { x: 70, y: 50, text: '250L buffer cylinder', type: 'new' },
        { x: 30, y: 80, text: 'Radiators require upgrade', type: 'changes' },
        { x: 50, y: 90, text: 'Flow temp: 50°C max', type: 'changes' }
      ],
      'combi': [
        { x: 50, y: 40, text: '30kW Combi Boiler', type: 'new' },
        { x: 30, y: 70, text: 'Existing radiators (adequate)', type: 'stays' }
      ],
      'heat-pump-ground': [
        { x: 40, y: 20, text: 'Ground collector (150m²)', type: 'new' },
        { x: 70, y: 50, text: '10kW GSHP + 250L cylinder', type: 'new' }
      ],
      'regular-boiler': [
        { x: 50, y: 30, text: '18kW Regular Boiler', type: 'new' },
        { x: 70, y: 60, text: 'F&E tanks (loft)', type: 'stays' }
      ]
    };

    return technicalAnnotations[systemType] || [];
  };

  const annotations = getAnnotations();

  return (
    <div className="system-diagram">
      {!imgOk && (
        <div style={{
          padding: '12px',
          marginBottom: '8px',
          backgroundColor: '#fff3cd',
          border: '1px solid #ffc107',
          borderRadius: '4px',
          color: '#856404',
          fontSize: '14px'
        }}>
          ⚠️ Diagram image missing (check assets path)
        </div>
      )}
      
      <img
        src={getDiagramPath()}
        alt={`${systemType} system diagram`}
        className="diagram-image"
        onError={() => setImgOk(false)}
      />

      <svg
        className="diagram-annotations"
        viewBox="0 0 100 100"
        preserveAspectRatio="xMidYMid meet"
      >
        {annotations.map((annotation, index) => (
          <g key={index}>
            <circle
              cx={annotation.x}
              cy={annotation.y}
              r="2"
              className={`annotation-dot ${annotation.type}`}
            />
            <text
              x={annotation.x + 3}
              y={annotation.y + 1}
              className={`annotation-label ${annotation.type}`}
            >
              {annotation.text}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
};

export default SystemDiagram;
